<template>
  <SettingsPageLayout>
    <DetailForm
      v-if="loaded"
      class="mt-6"
      title="Calendar"
      description="Share your availabilty based on your calendar. For help connecting your calendar, email support@humaniz.io."
      action="Save"
      hidecancel="true"
      @handle-action="updateUser"
    >
      <ul class="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <li class="col-span-1">
          <ConnectGoogleCalendarButton />
        </li>

        <li class="col-span-1">
          <div class="border border-gray-200 rounded-full">
            <div class="-mt-px flex">
              <div v-if="!currentUserCalendar.isMicrosoftAuthenticated" class="w-0 flex-1 flex">
                <form
                  :action="`/users/auth/microsoft?prompt=consent&login_hint=${currentUserStore.currentUserAccountDetails.username}`"
                  method="post"
                  class="cursor-pointer relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm leading-5 text-gray-700 font-medium border border-transparent rounded-full hover:text-gray-500 focus:outline-none focus:shadow-outline-indigo focus:border-indigo-300 focus:z-10 transition ease-in-out duration-150"
                >
                  <MicrosoftIcon />
                  <input type="hidden" name="authenticity_token" :value="csrf" />
                  <button type="submit">Connect Microsoft Calendar</button>
                </form>
              </div>
              <div v-else class="-ml-px w-0 flex-1 flex">
                <div
                  class="relative w-0 flex-1 inline-flex items-center justify-center py-4 text-sm leading-5 text-gray-700 font-medium border border-transparent rounded-full hover:text-gray-500 focus:outline-none focus:shadow-outline-indigo focus:border-indigo-300 focus:z-10 transition ease-in-out duration-150"
                >
                  <CheckmarkIcon />
                  <span class="ml-3">Microsoft Calendar Connected</span>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
      <SelectMenu
        label="Timezone"
        :options="timezones"
        :initial-value="user?.timezone"
        @selected-option="user.timezone = $event"
      />
      <SelectMenu
        label="Event Buffer"
        :options="eventBuffer"
        :initial-value="user.eventBuffer"
        @selected-option="user.eventBuffer = $event"
      />
      <p class="block text-sm leading-5 font-medium text-gray-700 mb-4">Event Availability</p>
      <div class="grid grid-cols-3 gap-x-6 gap-y-8 mb-4">
        <CheckboxInput
          class="mt-2 flex items-center"
          description="Sunday"
          :model-value="availability.sunday.isAvailable"
          @update:model-value="availability.sunday.isAvailable = $event"
        />
        <TimeInput
          :model-value="availability.sunday.startTime"
          @update:model-value="availability.sunday.startTime = $event"
        />
        <TimeInput
          :model-value="availability.sunday.endTime"
          @update:model-value="availability.sunday.endTime = $event"
        />
      </div>
      <div class="grid grid-cols-3 gap-x-6 gap-y-8 mb-4">
        <CheckboxInput
          class="mt-2 flex items-center"
          description="Monday"
          :model-value="availability.monday.isAvailable"
          @update:model-value="availability.monday.isAvailable = $event"
        />
        <TimeInput
          :model-value="availability.monday.startTime"
          @update:model-value="availability.monday.startTime = $event"
        />
        <TimeInput
          :model-value="availability.monday.endTime"
          @update:model-value="availability.monday.endTime = $event"
        />
      </div>
      <div class="grid grid-cols-3 gap-x-6 gap-y-8 mb-4">
        <CheckboxInput
          class="mt-2 flex items-center"
          description="Tuesday"
          :model-value="availability.tuesday.isAvailable"
          @update:model-value="availability.tuesday.isAvailable = $event"
        />
        <TimeInput
          :model-value="availability.tuesday.startTime"
          @update:model-value="availability.tuesday.startTime = $event"
        />
        <TimeInput
          :model-value="availability.tuesday.endTime"
          @update:model-value="availability.tuesday.endTime = $event"
        />
      </div>
      <div class="grid grid-cols-3 gap-x-6 gap-y-8 mb-4">
        <CheckboxInput
          class="mt-2 flex items-center"
          description="Wednesday"
          :model-value="availability.wednesday.isAvailable"
          @update:model-value="availability.wednesday.isAvailable = $event"
        />
        <TimeInput
          :model-value="availability.wednesday.startTime"
          @update:model-value="availability.wednesday.startTime = $event"
        />
        <TimeInput
          :model-value="availability.wednesday.endTime"
          @update:model-value="availability.wednesday.endTime = $event"
        />
      </div>
      <div class="grid grid-cols-3 gap-x-6 gap-y-8 mb-4">
        <CheckboxInput
          class="mt-2 flex items-center"
          description="Thursday"
          :model-value="availability.thursday.isAvailable"
          @update:model-value="availability.thursday.isAvailable = $event"
        />
        <TimeInput
          :model-value="availability.thursday.startTime"
          @update:model-value="availability.thursday.startTime = $event"
        />
        <TimeInput
          :model-value="availability.thursday.endTime"
          @update:model-value="availability.thursday.endTime = $event"
        />
      </div>
      <div class="grid grid-cols-3 gap-x-6 gap-y-8 mb-4">
        <CheckboxInput
          class="mt-2 flex items-center"
          description="Friday"
          :model-value="availability.friday.isAvailable"
          @update:model-value="availability.friday.isAvailable = $event"
        />
        <TimeInput
          :model-value="availability.friday.startTime"
          @update:model-value="availability.friday.startTime = $event"
        />
        <TimeInput
          :model-value="availability.friday.endTime"
          @update:model-value="availability.friday.endTime = $event"
        />
      </div>
      <div class="grid grid-cols-3 gap-x-6 gap-y-8">
        <CheckboxInput
          class="mt-2 flex items-center"
          description="Saturday"
          :model-value="availability.saturday.isAvailable"
          @update:model-value="availability.saturday.isAvailable = $event"
        />
        <TimeInput
          :model-value="availability.saturday.startTime"
          @update:model-value="availability.saturday.startTime = $event"
        />
        <TimeInput
          :model-value="availability.saturday.endTime"
          @update:model-value="availability.saturday.endTime = $event"
        />
      </div>
    </DetailForm>
  </SettingsPageLayout>
</template>

<script>
import { cloneDeep } from 'lodash';
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import RailsUtility from '../../../utilities/rails.ts';
import DetailForm from '../../shared/DetailForm.vue';
import TimeInput from '../../shared/TimeInput.vue';
import CheckboxInput from '../../shared/CheckboxInput.vue';
import SelectMenu from '../../shared/SelectMenu.vue';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import { useUserCalendarStore } from '../../../stores/userCalendar.ts';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import MicrosoftIcon from '@/components/oauth/MicrosoftIcon.vue';
import CheckmarkIcon from '@/components/oauth/CheckmarkIcon.vue';
import ConnectGoogleCalendarButton from '@/components/oauth/ConnectGoogleCalendarButton.vue';

export default {
  components: {
    SettingsPageLayout,
    DetailForm,
    TimeInput,
    CheckboxInput,
    SelectMenu,
    ConnectGoogleCalendarButton,
    MicrosoftIcon,
    CheckmarkIcon,
  },
  setup() {
    const currentUserStore = useCurrentUserStore();
    const userCalendarStore = useUserCalendarStore();
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { userCalendarStore, currentUserStore, loadingSpinnerStore };
  },
  data() {
    return {
      availability: {},
      timezones: [
        'Hawaii',
        'Alaska',
        'Pacific Time (US & Canada)',
        'Arizona',
        'Mountain Time (US & Canada)',
        'Central Time (US & Canada)',
        'Eastern Time (US & Canada)',
        'Puerto Rico',
      ],
      eventBuffer: ['1 hours', '2 hours', '3 hours', '4 hours', '8 hours', '12 hours', '24 hours'],
      loaded: false,
    };
  },
  computed: {
    user() {
      return this.currentUserStore.currentUser;
    },
    currentUserAvailability() {
      return this.userCalendarStore.currentUserAvailability;
    },
    currentUserCalendar() {
      return this.userCalendarStore.currentUserCalendar;
    },
    csrf() {
      return document.querySelectorAll('[name="csrf-token"]')[0].content;
    },
  },
  async mounted() {
    await this.getUser();
  },
  methods: {
    async getUser() {
      await this.currentUserStore.loadCurrentUser();
      await this.userCalendarStore.loadUserCalendarData();
      this.availability = cloneDeep(this.currentUserAvailability);
      await this.getCalendarConnection();
    },
    async getCalendarConnection() {
      this.loadingSpinnerStore.showLoadingSpinner();
      await this.userCalendarStore.loadCalendarAuthStatus(this.user);
      this.loadingSpinnerStore.hideLoadingSpinner();
      this.loaded = true;
    },
    updateUser() {
      const self = this;
      this.loadingSpinnerStore.showLocalLoadingSpinner();

      // Convert the availability object to the format expected by the server
      const availabilityArray = Object.entries(self.availability).map(([dayOfWeek, dayData]) => ({
        day_of_week: dayOfWeek,
        start_time: dayData.startTime,
        end_time: dayData.endTime,
        is_available: dayData.isAvailable,
      }));

      fetch(`/api/user_calendars/${self.user?.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          timezone: self.user.timezone,
          event_buffer: RailsUtility.convertToEnumFormat(self.user.eventBuffer),
          availability: availabilityArray.reduce((acc, curr) => {
            acc[curr.day_of_week] = {
              startTime: curr.start_time,
              endTime: curr.end_time,
              isAvailable: curr.is_available,
            };
            return acc;
          }, {}),
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          this.loadingSpinnerStore.hideLocalLoadingSpinner();
          if (data.status != 'error') {
            // First, update the store with the new availability directly
            self.userCalendarStore.setCurrentUserAvailability({
              availability: availabilityArray,
            });

            // Also update timezone and event buffer in the store
            this.currentUserStore.setCurrentUser({
              currentUser: self.user,
              timezone: self.user.timezone,
              teamSlug: self.user.teamSlug,
              eventBuffer: self.user.eventBuffer,
            });

            // Then reload the user data to ensure everything is in sync
            this.currentUserStore.loadCurrentUser();

            toast.success('Calendar updated', {
              action: { label: 'Dismiss' },
              description: 'Your Calendar was successfully updated. Use the button below to dismiss this alert.',
            });
          } else {
            toast.error('Calendar update error', {
              action: { label: 'Dismiss' },
              description: data.message,
            });
          }
        })
        .catch(error => {
          this.loadingSpinnerStore.hideLocalLoadingSpinner();
          toast.error('Calendar update error', {
            action: { label: 'Dismiss' },
            description: 'An error occurred while updating your calendar settings.',
          });
          console.error('Calendar update error:', error);
        });
    },
  },
};
</script>
